import { loadArt } from '../art';
import type { ArtRef } from '../types';

/**
 * The frame every screen of the week is built in.
 *
 * The spec's first global rule: everything a child touches is in the bottom
 * half of the panel, and the top half is for looking. An 86-inch panel hangs at
 * an adult's height and a TK A child is a metre tall. So a screen here is two
 * zones and a horizon between them:
 *
 *   sky      what the class watches - the animal, the question, the letter
 *   ground   what a child reaches - cards, trays, lanes
 *
 * The horizon is drawn, softly, as the edge of a lawn. It is the reach line,
 * and making it the ground the animals stand on turns a layout rule into the
 * same yard that Halaman Kita is built on.
 */

export const escapeHtml = (value: string): string =>
  value.replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c,
  );

export interface Scene {
  root: HTMLElement;
  sky: HTMLElement;
  ground: HTMLElement;
  remove(): void;
}

export interface SceneOptions {
  /** Extra class on the root, usually the kind: `scene--feed`. */
  kind: string;
  /** How much of the height the sky takes, 0-1. Default 0.44. */
  sky?: number;
  /** Dark sky, for the reveal and the calm screens. */
  night?: boolean;
  /** No touch anywhere - the whole-class screens where children stand. */
  handsOff?: boolean;
}

export function createScene(host: HTMLElement, options: SceneOptions): Scene {
  const root = document.createElement('div');
  root.className = `scene scene--${options.kind}`;
  if (options.night) root.classList.add('scene--night');
  if (options.handsOff) root.classList.add('scene--hands-off');
  root.style.setProperty('--sky', `${Math.round((options.sky ?? 0.44) * 100)}%`);
  root.innerHTML = `
    <div class="scene__sky"></div>
    <div class="scene__horizon" aria-hidden="true"></div>
    <div class="scene__ground"></div>
  `;
  host.append(root);
  return {
    root,
    sky: root.querySelector('.scene__sky') as HTMLElement,
    ground: root.querySelector('.scene__ground') as HTMLElement,
    remove: () => root.remove(),
  };
}

/**
 * An inlined drawing, with an ArtRef's repaint, pose and focus applied.
 *
 * Inlined rather than an <img>, because every screen that matters reaches into
 * the drawing: a pose moves the ears, a focus lights the whiskers, a repaint
 * turns the orange cat white. The id prefix goes on the wrapper as
 * `data-hewan`, so a pose can be tuned per animal without the drawing knowing.
 */
export async function artElement(ref: ArtRef | string, className = 'art'): Promise<HTMLElement> {
  const r: ArtRef = typeof ref === 'string' ? { art: ref } : ref;
  const el = document.createElement('div');
  el.className = className;
  el.dataset['art'] = r.art;
  el.dataset['hewan'] = r.art.split('/')[0] ?? '';

  const source = await loadArt(r.art);
  if (!source) {
    el.classList.add('art--missing');
    el.innerHTML = `<span>${escapeHtml(r.art)}</span>`;
    return el;
  }

  el.innerHTML = source;
  const svg = el.querySelector('svg');
  if (!svg) return el;
  applyRef(svg, r);
  return el;
}

export function applyRef(svg: SVGSVGElement, r: ArtRef): void {
  if (r.paint) paint(svg, r.paint);
  if (r.pose) svg.dataset['pose'] = r.pose;
  else delete svg.dataset['pose'];
  svg.querySelectorAll('.is-focus').forEach((g) => g.classList.remove('is-focus'));
  if (r.focus) {
    svg.dataset['focus'] = r.focus;
    svg.querySelector(`[data-region="${CSS.escape(r.focus)}"]`)?.classList.add('is-focus');
  } else {
    delete svg.dataset['focus'];
  }
}

/**
 * Frames the drawing on one of its regions: a "kumis" word card is the cat's
 * whiskers filling the card, not a small cat with a faint glow somewhere on
 * it. Must run once the drawing is in the document, since it measures.
 */
export function zoomToRegion(svg: SVGSVGElement | null, region: string, pad = 0.35): void {
  const group = svg?.querySelector<SVGGraphicsElement>(`[data-region="${CSS.escape(region)}"]`);
  if (!svg || !group) return;
  const box = group.getBBox();
  if (!box.width || !box.height) return;
  // A long thin part (whiskers span the whole face) framed square would take
  // in most of the animal; crop it tighter and let its ends run off the card.
  const long = Math.max(box.width, box.height);
  const short = Math.min(box.width, box.height);
  const side = (short * 2.2 < long ? long * 0.62 : long) * (1 + pad);
  const x = box.x + box.width / 2 - side / 2;
  const y = box.y + box.height / 2 - side / 2;
  svg.setAttribute('viewBox', `${x} ${y} ${side} ${side}`);
  // Framed on the part already; dimming the rest as well only washes it out.
  delete svg.dataset['focus'];
  // Drawings may overflow their box elsewhere (a wagging tail); a cropped one
  // must not, or the rest of the animal spills over the next card.
  svg.style.overflow = 'hidden';
}

/** region -> colour on every `data-paint` shape of that region. */
export function paint(svg: SVGSVGElement, painting: Record<string, string>): void {
  for (const [region, colour] of Object.entries(painting)) {
    svg
      .querySelectorAll<SVGElement>(`[data-region="${CSS.escape(region)}"] [data-paint]`)
      .forEach((shape) => {
        shape.style.fill = colour;
      });
  }
}

/** The region under a point in an inlined drawing, if any. */
export function regionAt(x: number, y: number, within: Element): SVGGElement | null {
  const hit = document.elementFromPoint(x, y);
  if (!hit || !within.contains(hit)) return null;
  return hit.closest<SVGGElement>('[data-region]');
}

/**
 * N lanes across the ground, one per child.
 *
 * Each lane is a region of the glass that belongs to one child. The spec's
 * MULTI screens are four of these; Mode Guru can make it fewer for a smaller
 * panel or a smaller group. Lanes are numbered so a child can find their own
 * work again on Halaman Kita.
 */
export function createLanes(ground: HTMLElement, count: number): HTMLElement[] {
  const wrap = document.createElement('div');
  wrap.className = 'lajur-set';
  wrap.style.setProperty('--lanes', String(count));
  const lanes: HTMLElement[] = [];
  for (let i = 0; i < count; i++) {
    const lane = document.createElement('div');
    lane.className = 'lajur';
    lane.dataset['lane'] = String(i + 1);
    wrap.append(lane);
    lanes.push(lane);
  }
  ground.append(wrap);
  return lanes;
}

/**
 * The mascot, in one of its states. Every state is CSS on the one drawing:
 *
 *   diam        idle, breathing
 *   pandu       guiding - leaning in, one arm out
 *   rayakan     celebrating - a hop, both arms up
 *   semangati   encouraging - a nod
 *   tenang      calm - slow, eyes half closed
 *   gerak-*     the demonstrations on Gerak Bersama
 */
export async function mascot(state = 'diam'): Promise<HTMLElement> {
  const el = await artElement('maskot/tunas', 'tunas');
  el.dataset['state'] = state;
  return el;
}

export function setMascot(el: HTMLElement | null, state: string): void {
  if (el) el.dataset['state'] = state;
}

/** A tiny speaker glyph for "play the sound" buttons. UI, not art. */
export const ICON_SOUND = `
  <svg viewBox="0 0 48 48" aria-hidden="true"><path d="M8 18h8l11-9v30l-11-9H8z" fill="currentColor"/>
  <path d="M33 16c3 2.3 4.5 5 4.5 8s-1.5 5.7-4.5 8M38 10c5 3.8 7.5 8.4 7.5 14S43 34.2 38 38" fill="none" stroke="currentColor" stroke-width="4" stroke-linecap="round"/></svg>`;

export const ICON_REPLAY = `
  <svg viewBox="0 0 48 48" aria-hidden="true"><path d="M14 10v28l24-14z" fill="currentColor"/></svg>`;

/** Resolves after `ms`, or immediately under reduced motion. */
export function wait(ms: number): Promise<void> {
  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  return new Promise((resolve) => window.setTimeout(resolve, reduced ? Math.min(ms, 60) : ms));
}
